import { Input } from '@/components/_ui/input';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalContent,
  ModalFooter,
  ModalCloseButton,
  ModalConfirmButton,
} from '@/components/_ui/modal';
import { useUrlSpaceMemberQuery } from '@/features/ticketings/ticketing-user/api/get-url-space-member.api';
import { useTicketingModalRootActions } from '@/features/ticketings/_shared/components/ticketing-modal-root/ticketing-modal-root.store';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { handleDomBubbleEvent } from '@/utils/dom';
import { memo, useCallback, useMemo } from 'react';

export type TShareSpaceModalType = 'ShareSpaceModal';
export interface IShareSpaceModalProps {
  spaceId: string;
}
const ShareSpaceModal = memo(({ spaceId }: IShareSpaceModalProps) => {
  // HOOKS
  const showToast = useCustomToast();
  const ticketingModalActions = useTicketingModalRootActions();
  const { data: urlSpaceMemberData, isLoading: isLoadingUrlSpaceMemberData } = useUrlSpaceMemberQuery({
    params: {
      spaceId: spaceId ?? '',
    },
    queryConfig: {
      enabled: !!spaceId,
    },
  });

  // HELPERS
  const isOpenShareSpaceModal = useMemo(() => {
    return ticketingModalActions.checkIsOpenModal('ShareSpaceModal');
  }, [ticketingModalActions]);

  const invitationLink = useMemo(() => {
    return urlSpaceMemberData?.payload?.accept_invitation_link;
  }, [urlSpaceMemberData]);

  // HANDLERS
  const handleClose = useCallback(() => {
    ticketingModalActions.closeModal('ShareSpaceModal');
  }, [ticketingModalActions]);

  const handleCopy = useCallback(async () => {
    try {
      if (invitationLink) {
        await navigator.clipboard.writeText(invitationLink);
        showToast({
          type: 'success',
          message: 'Link copied to clipboard!',
        });
      }
    } catch (_error) {
      showToast({
        type: 'error',
        message: 'Failed to copy link!',
      });
    }
  }, [invitationLink, showToast]);

  return (
    <Modal
      isOpen={isOpenShareSpaceModal}
      lockBodyScroll
      onClose={handleClose}
      isCloseOutside={false}
      className="md:w-[500px] w-[345px]"
    >
      <ModalHeader>
        <ModalTitle>Share this Space</ModalTitle>
        <ModalDescription>
          Sharing this Space with all views. Anyone with the link can join as a member
        </ModalDescription>
      </ModalHeader>
      <ModalContent>
        <div className="grid gap-[16px]">
          <div className="relative flex gap-2">
            <Input
              id="space-link"
              value={isLoadingUrlSpaceMemberData ? 'Loading...' : invitationLink || ''}
              placeholder="Generated link Invitation"
              className="pr-[76px] text-[#9D9D9C] text-[12px]"
              readOnly
              disabled
            />
            <ModalConfirmButton
              disabled={isLoadingUrlSpaceMemberData}
              type="button"
              className="absolute cursor-pointer right-1 top-1/2 -translate-y-1/2 h-[30px] px-[12px]"
              onClick={(e) => {
                handleDomBubbleEvent(e);
                handleCopy();
              }}
            >
              Copy
            </ModalConfirmButton>
          </div>
        </div>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
        >
          Cancel
        </ModalCloseButton>
      </ModalFooter>
    </Modal>
  );
});
ShareSpaceModal.displayName = 'ShareSpaceModal';

export default ShareSpaceModal;
